import React, { useState } from 'react';
import classnames from 'classnames';
import { Field, FieldConfig } from 'formik';
import { Visibility, VisibilityOff } from '@material-ui/icons';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { PasswordHelpers } from '../PasswordHelpers/PasswordHelpers';
import { useField } from '../hooks/useField';

import styles from './FieldCustom.module.scss';

type FieldCustomProps = {
  as?: FieldConfig['as'];
  component?: FieldConfig['component'];
  disabled?: boolean;
  extraStyle?: ExtraStyleType;
  label?: string;
  name: string;
  required?: boolean;
  type?: string;
  id?: string;
  isErrorVisible?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

/**
 * @deprecated Use FormikTextRow, FormikRadioGroupRow or FormikDateInputsRow component.
 */
export function FieldCustom({
  as,
  component,
  children,
  disabled,
  extraStyle,
  label,
  name,
  required,
  type,
  id,
  isErrorVisible = true,
  ...props
}: React.PropsWithChildren<FieldCustomProps>): JSX.Element {
  const [field, meta] = useField(name);
  const { value } = field;
  const { error, apiError, touched } = meta;
  const [showPassword, setShowPassword] = useState(false);
  const isError = (error && touched) || apiError;
  const isSuccess = !isError && touched && value !== '';

  const handleClickPassword = () => {
    setShowPassword(prevShowPassword => !prevShowPassword);
  };

  return (
    <div
      className={classnames(styles.container, {
        [`${extraStyle?.container}`]: extraStyle?.container,
      })}
    >
      {label && (
        <div
          className={classnames(styles.label, {
            [`${extraStyle?.label}`]: extraStyle?.label,
          })}
        >
          <label>
            {label}
            {required && <span>*</span>}
          </label>
        </div>
      )}
      <div
        className={classnames(styles.fieldContainer, {
          [`${extraStyle?.fieldContainer}`]: extraStyle?.fieldContainer,
        })}
      >
        <Field
          as={as}
          component={component}
          className={classnames(styles.field, {
            [styles.disabled]: disabled,
            [styles.error]: isError,
            [styles.success]: isSuccess,
            [`${extraStyle?.field}`]: extraStyle?.field,
          })}
          type={showPassword ? 'text' : type}
          disabled={disabled}
          {...field}
          {...props}
        >
          {children}
        </Field>
        {type === 'password' && (
          <div
            className={classnames(styles.passwordIcon, {
              [styles.errorPasswordIcon]: error && touched,
              [styles.successPasswordIcon]: !error && touched && value !== '',
            })}
            onMouseDown={handleClickPassword}
          >
            {showPassword ? <Visibility /> : <VisibilityOff />}
          </div>
        )}
        {id === 'PasswordWithHelper' && (
          <PasswordHelpers
            name={name}
            classes={{ container: styles.passwordHelperContainer }}
          />
        )}
        {isErrorVisible && (
          <ErrorMessage
            name={name}
            additionalClass={extraStyle?.labelErrorMessage}
          />
        )}
      </div>
    </div>
  );
}

export type ComponentType = {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  form: any;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  field: any;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type ExtraStyleType = {
  container?: string;
  field?: string;
  fieldContainer?: string;
  label?: string;
  labelErrorMessage?: string;
};
